import jsPDF from 'jspdf';

import { ISubscriptionChildDetails } from '@/types';
import { formatSecondsToDuration } from '@/utils';

/* -------------------- TYPES -------------------- */

export interface IPdfLabels {
  title: string;
  fullName: string;
  gender: string;
  dob: string;
  motherTongue: string;
  dialect: string;
  learningLevel: string;
  mode: string;
  age: string;
  years: string;
  status: string;
  statusValue: string;
  linkedEntities: string;
  progressSummary: string;
  overallProgress: string;
  starsEarned: string;
  gamesCompleted: string;
  avgScreenTime: string;
  mins: string;
  activity: string;
  noDataFound: string;
  licenseKey: string;
  assignedTeacher: string;
  licenseExpiry: string;
  learningStageLabel: string;
  subscriptionTypeLabel: string;
  childrenLabel: string;
  learningDetails: string;
}

export interface IPdfStatItem {
  label: string;
  value: string;
}

/* -------------------- COLORS -------------------- */

const COLORS = {
  brandDefault: '#9333ea',
  brandLight50: '#faf5ff',
  neutralPrimary: '#181b25',
  neutralSecondary: '#525866',
  neutralLight100: '#f2f5f8',
  neutralLight200: '#e1e4ea',
  neutral200: '#e5e5e5',
  successPrimary: '#16a34a',
  successLight50: '#f0fdf4',
  warningSecondary: '#d97706',
  warningLight100: '#fef3c7',
  indigoSecondary: '#4f46e5',
  indigoLight50: '#eef2ff',
  white: '#ffffff',
} as const;

const STAGE_COLORS: Record<number, { bg: string; text: string }> = {
  1: { bg: '#faf5ff', text: '#9333ea' },
  2: { bg: '#f0fdf4', text: '#16a34a' },
  3: { bg: '#eef2ff', text: '#4f46e5' },
  4: { bg: '#fff7ed', text: '#ea580c' },
};

/* -------------------- HELPERS -------------------- */

/** Font name resolved at runtime — Amiri for Arabic/RTL, Aptos for English. */
let PDF_FONT_NAME = 'Aptos';

/** Check if a string contains Arabic/Hebrew characters. */
function containsRTLChars(text: string): boolean {
  return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\u0590-\u05FF]/.test(
    text
  );
}

/**
 * Wrapper around pdf.text() that handles mixed LTR/RTL content correctly.
 * Sets R2L per-call based on whether the text contains Arabic characters.
 * English text (names, numbers, keys) always renders LTR even in RTL documents.
 */
function pdfText(
  pdf: jsPDF,
  text: string | string[],
  x: number,
  y: number,
  options?: { align?: 'left' | 'center' | 'right' }
) {
  const textStr = Array.isArray(text) ? text.join(' ') : text;
  const hasRTL = containsRTLChars(textStr);
  pdf.setR2L(hasRTL);
  pdf.text(text, x, y, options);
}

/**
 * Re-apply the correct font after every setSize() call.
 */
function applyFont(
  pdf: jsPDF,
  size?: number,
  weight: 'normal' | 'bold' = 'normal'
) {
  if (size) pdf.setFontSize(size);
  pdf.setFont(PDF_FONT_NAME, weight);
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

function checkPageBreak(
  pdf: jsPDF,
  currentY: number,
  neededHeight: number,
  margin: number,
  pageHeight: number
): number {
  if (currentY + neededHeight > pageHeight - margin) {
    pdf.addPage();
    return margin;
  }
  return currentY;
}

function drawBadge(
  pdf: jsPDF,
  text: string,
  x: number,
  y: number,
  bgColor: string,
  textColor: string,
  fontSize = 8
): number {
  applyFont(pdf, fontSize);
  const textW = pdf.getTextDimensions(text).w;
  const paddingX = 4;
  const paddingY = 2;
  const badgeW = textW + paddingX * 2;
  const badgeH = fontSize * 0.4 + paddingY * 2;

  pdf.setFillColor(...hexToRgb(bgColor));
  pdf.roundedRect(
    x,
    y - badgeH / 2,
    badgeW,
    badgeH,
    badgeH / 2,
    badgeH / 2,
    'F'
  );

  applyFont(pdf, fontSize);
  pdf.setTextColor(...hexToRgb(textColor));
  pdfText(pdf, text, x + paddingX, y + fontSize * 0.12);

  return badgeW;
}

function drawProgressBar(
  pdf: jsPDF,
  x: number,
  y: number,
  width: number,
  height: number,
  percentage: number,
  bgColor: string,
  fillColor: string
) {
  const radius = height / 2;

  pdf.setFillColor(...hexToRgb(bgColor));
  pdf.roundedRect(x, y, width, height, radius, radius, 'F');

  if (percentage > 0) {
    const fillWidth = Math.max((width * percentage) / 100, height);
    pdf.setFillColor(...hexToRgb(fillColor));
    pdf.roundedRect(x, y, fillWidth, height, radius, radius, 'F');
  }
}

function drawStatCard(
  pdf: jsPDF,
  x: number,
  y: number,
  width: number,
  label: string,
  value: string,
  bgColor: string,
  textColor: string,
  height: number
) {
  pdf.setFillColor(...hexToRgb(bgColor));
  pdf.roundedRect(x, y, width, height, 3, 3, 'F');

  applyFont(pdf, 7);
  pdf.setTextColor(...hexToRgb(textColor));
  const lines = pdf.splitTextToSize(label, width - 6);
  const labelHeight = lines.length * 3.5;
  pdfText(pdf, lines, x + width / 2, y + 6, { align: 'center' });

  applyFont(pdf, 14, 'bold');
  pdfText(pdf, value, x + width / 2, y + labelHeight + 12, { align: 'center' });
}

function drawInitialsCircle(
  pdf: jsPDF,
  centerX: number,
  centerY: number,
  radius: number,
  name: string,
  bgColor: string,
  textColor: string
) {
  pdf.setFillColor(...hexToRgb(bgColor));
  pdf.circle(centerX, centerY, radius, 'F');

  const initial = (name || '?').charAt(0).toUpperCase();
  applyFont(pdf, radius * 1.2);
  pdf.setTextColor(...hexToRgb(textColor));
  pdfText(pdf, initial, centerX, centerY + radius * 0.23, { align: 'center' });
}

async function loadImage(url: string): Promise<string> {
  // Rewrite cross-origin CDN URLs to go through the local proxy (bypasses CORS).
  // The Vite dev server proxies /asset-proxy/* → https://sg-edtech-assets.solz.me/*
  let fetchUrl = url;
  try {
    const parsed = new URL(url);
    if (parsed.hostname === 'sg-edtech-assets.solz.me') {
      fetchUrl = '/asset-proxy' + parsed.pathname + parsed.search;
    }
  } catch {
    // Not a valid URL, use as-is
  }

  // Strategy 1: fetch via proxy (no CORS issue since same-origin)
  try {
    const response = await fetch(fetchUrl);
    if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
    const blob = await response.blob();
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('FileReader failed'));
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn('[PDF] Strategy 1 (proxy fetch) failed:', e);
  }

  // Strategy 2: direct fetch with CORS (fallback for non-proxied URLs)
  try {
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'omit',
    });
    if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
    const blob = await response.blob();
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('FileReader failed'));
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn('[PDF] Strategy 2 (direct CORS fetch) failed:', e);
  }

  throw new Error('All image loading strategies failed for: ' + url);
}

/**
 * Crop a base64 image into a circle (equivalent to CSS rounded-full).
 * Returns a PNG data URL with transparent corners.
 */
function makeCircularImage(dataUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const sizePx = Math.max(img.naturalWidth, img.naturalHeight, 200);
      const canvas = document.createElement('canvas');
      canvas.width = sizePx;
      canvas.height = sizePx;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context failed'));
        return;
      }
      // Draw circular clipping path
      ctx.beginPath();
      ctx.arc(sizePx / 2, sizePx / 2, sizePx / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      // Draw the image centered and covering the circle
      const scale = Math.max(
        sizePx / img.naturalWidth,
        sizePx / img.naturalHeight
      );
      const w = img.naturalWidth * scale;
      const h = img.naturalHeight * scale;
      ctx.drawImage(img, (sizePx - w) / 2, (sizePx - h) / 2, w, h);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error('Circular crop image load failed'));
    img.src = dataUrl;
  });
}

/**
 * Fetch a font from /fonts and return as base64 string.
 */
async function fetchFontBase64(path: string): Promise<string> {
  const response = await fetch(path);
  if (!response.ok) throw new Error(`Font fetch failed: ${path}`);
  const buffer = await response.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/* -------------------- SECTION DRAWERS -------------------- */

function drawTitle(
  pdf: jsPDF,
  title: string,
  y: number,
  margin: number,
  contentWidth: number
): number {
  applyFont(pdf, 16, 'bold');
  pdf.setTextColor(...hexToRgb(COLORS.neutralPrimary));
  pdfText(pdf, title, margin + contentWidth / 2, y + 6, { align: 'center' });
  y += 10;

  pdf.setDrawColor(...hexToRgb(COLORS.neutralLight200));
  pdf.setLineWidth(0.3);
  pdf.line(margin, y, margin + contentWidth, y);
  y += 5;
  return y;
}

async function drawProfileHeader(
  pdf: jsPDF,
  data: ISubscriptionChildDetails,
  labels: IPdfLabels,
  avatarImg: string | null,
  y: number,
  margin: number,
  contentWidth: number,
  isRTL: boolean
): Promise<number> {
  const centerX = margin + contentWidth / 2;
  const avatarRadius = 10;

  if (avatarImg) {
    try {
      // Pre-crop image to a circle using canvas (equivalent to CSS rounded-full)
      const circularImg = await makeCircularImage(avatarImg);
      pdf.addImage(
        circularImg,
        'PNG',
        centerX - avatarRadius,
        y,
        avatarRadius * 2,
        avatarRadius * 2
      );
    } catch {
      drawInitialsCircle(
        pdf,
        centerX,
        y + avatarRadius,
        avatarRadius,
        data.full_name,
        COLORS.brandDefault,
        COLORS.white
      );
    }
  } else {
    drawInitialsCircle(
      pdf,
      centerX,
      y + avatarRadius,
      avatarRadius,
      data.full_name,
      COLORS.brandDefault,
      COLORS.white
    );
  }
  y += avatarRadius * 2 + 8;

  // Name — always centred
  applyFont(pdf, 14, 'bold');
  pdf.setTextColor(...hexToRgb(COLORS.neutralPrimary));
  pdfText(pdf, data.full_name, centerX, y, { align: 'center' });
  y += 7;

  // Badges — centred
  applyFont(pdf, 8);
  const stageColors = data.learning_stage
    ? STAGE_COLORS[data.learning_stage]
    : null;
  const stageLabelW = stageColors
    ? pdf.getTextDimensions(labels.learningStageLabel).w + 8
    : 0;
  const subLabelW = pdf.getTextDimensions(labels.subscriptionTypeLabel).w + 8;
  const gap = 3;
  const totalBadgeW = (stageColors ? stageLabelW + gap : 0) + subLabelW;
  let badgeX = centerX - totalBadgeW / 2;

  if (stageColors) {
    const w = drawBadge(
      pdf,
      labels.learningStageLabel,
      badgeX,
      y,
      stageColors.bg,
      stageColors.text
    );
    badgeX += w + gap;
  }
  drawBadge(
    pdf,
    labels.subscriptionTypeLabel,
    badgeX,
    y,
    COLORS.neutralLight100,
    COLORS.neutralSecondary
  );
  y += 7;

  // Age / Gender
  applyFont(pdf, 9);
  pdf.setTextColor(...hexToRgb(COLORS.neutralSecondary));
  const ageGenderText = isRTL
    ? `${data.gender ?? '-'} :${labels.gender}    |    ${labels.years} ${data.age ?? 0} :${labels.age}`
    : `${labels.age}: ${data.age ?? 0} ${labels.years}    |    ${labels.gender}: ${data.gender ?? '-'}`;
  pdfText(pdf, ageGenderText, centerX, y, { align: 'center' });
  y += 8;

  return y;
}

function drawStatusRow(
  pdf: jsPDF,
  data: ISubscriptionChildDetails,
  labels: IPdfLabels,
  y: number,
  margin: number,
  contentWidth: number,
  isRTL: boolean
): number {
  const pageWidth = 210;
  const isActive = data.status === 'active';
  const dotOffset = 3;
  const textOffset = 8;

  if (isRTL) {
    pdf.setFillColor(
      ...hexToRgb(isActive ? COLORS.successPrimary : COLORS.warningSecondary)
    );
    pdf.circle(pageWidth - margin - dotOffset, y + 2, 2, 'F');

    applyFont(pdf, 10);
    pdf.setTextColor(...hexToRgb(COLORS.neutralSecondary));
    const statusLabel = `${labels.status}: `;
    pdfText(pdf, statusLabel, pageWidth - margin - textOffset, y + 3, {
      align: 'right',
    });

    const statusLabelW = pdf.getTextDimensions(statusLabel).w;
    pdf.setTextColor(...hexToRgb(COLORS.neutralPrimary));
    pdfText(
      pdf,
      labels.statusValue,
      pageWidth - margin - textOffset - statusLabelW,
      y + 3,
      { align: 'right' }
    );
  } else {
    pdf.setFillColor(
      ...hexToRgb(isActive ? COLORS.successPrimary : COLORS.warningSecondary)
    );
    pdf.circle(margin + dotOffset, y + 2, 2, 'F');

    applyFont(pdf, 10);
    pdf.setTextColor(...hexToRgb(COLORS.neutralSecondary));
    pdfText(pdf, `${labels.status}: `, margin + textOffset, y + 3);

    const statusLabelW = pdf.getTextDimensions(`${labels.status}: `).w;
    pdf.setTextColor(...hexToRgb(COLORS.neutralPrimary));
    pdfText(pdf, labels.statusValue, margin + textOffset + statusLabelW, y + 3);
  }

  y += 8;
  pdf.setDrawColor(...hexToRgb(COLORS.neutralLight200));
  pdf.setLineWidth(0.2);
  pdf.line(margin, y, margin + contentWidth, y);
  y += 6;

  return y;
}

function drawStatsGrid(
  pdf: jsPDF,
  stats: IPdfStatItem[],
  y: number,
  margin: number,
  contentWidth: number,
  isRTL: boolean
): number {
  const colWidth = contentWidth / 2;
  const rowHeight = 14;
  const startY = y;
  const pageWidth = 210;

  for (let i = 0; i < stats.length; i++) {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const itemY = startY + row * rowHeight;

    let x: number;
    if (isRTL) {
      x = col === 0 ? pageWidth - margin - colWidth : margin;
    } else {
      x = margin + col * colWidth;
    }

    pdf.setFillColor(...hexToRgb(COLORS.neutralSecondary));
    if (isRTL) {
      pdf.circle(x + colWidth - 3, itemY + 3, 1.5, 'F');
    } else {
      pdf.circle(x + 3, itemY + 3, 1.5, 'F');
    }

    applyFont(pdf, 8);
    pdf.setTextColor(...hexToRgb(COLORS.neutralSecondary));
    if (isRTL) {
      pdfText(pdf, stats[i].label, x + colWidth - 8, itemY + 2, {
        align: 'right',
      });
    } else {
      pdfText(pdf, stats[i].label, x + 8, itemY + 2);
    }

    applyFont(pdf, 10, 'bold');
    pdf.setTextColor(...hexToRgb(COLORS.neutralPrimary));
    const maxValueWidth = colWidth - 12;
    const valueLines = pdf.splitTextToSize(stats[i].value, maxValueWidth);
    if (isRTL) {
      pdfText(pdf, valueLines[0], x + colWidth - 8, itemY + 7, {
        align: 'right',
      });
    } else {
      pdfText(pdf, valueLines[0], x + 8, itemY + 7);
    }
  }

  const totalRows = Math.ceil(stats.length / 2);
  y = startY + totalRows * rowHeight + 4;
  return y;
}

function drawLinkedEntities(
  pdf: jsPDF,
  data: ISubscriptionChildDetails,
  labels: IPdfLabels,
  y: number,
  margin: number,
  contentWidth: number,
  pageHeight: number,
  isRTL: boolean
): number {
  const pageWidth = 210;

  // Section header
  applyFont(pdf, 12, 'bold');
  pdf.setTextColor(...hexToRgb(COLORS.neutralPrimary));
  if (isRTL) {
    pdfText(pdf, labels.linkedEntities, pageWidth - margin, y + 4, {
      align: 'right',
    });
    const headerW = pdf.getTextDimensions(labels.linkedEntities).w;
    const count = String(data.linked_entities?.length ?? 0);
    drawBadge(
      pdf,
      count,
      pageWidth - margin - headerW - 12,
      y + 2,
      COLORS.neutralLight100,
      COLORS.neutralSecondary,
      8
    );
  } else {
    pdfText(pdf, labels.linkedEntities, margin, y + 4);
    const count = String(data.linked_entities?.length ?? 0);
    const headerW = pdf.getTextDimensions(labels.linkedEntities).w;
    drawBadge(
      pdf,
      count,
      margin + headerW + 4,
      y + 2,
      COLORS.neutralLight100,
      COLORS.neutralSecondary,
      8
    );
  }
  y += 12;

  const entities = data.linked_entities ?? [];

  for (const entity of entities) {
    const cardHeight = 38;
    y = checkPageBreak(pdf, y, cardHeight, margin, pageHeight);

    pdf.setFillColor(...hexToRgb(COLORS.brandLight50));
    pdf.roundedRect(margin, y, contentWidth, cardHeight, 4, 4, 'F');

    // Icon square
    const iconX = isRTL ? margin + contentWidth - 12 : margin + 5;
    pdf.setFillColor(...hexToRgb(COLORS.brandDefault));
    pdf.roundedRect(iconX, y + 4, 7, 7, 1.5, 1.5, 'F');
    applyFont(pdf, 6);
    pdf.setTextColor(...hexToRgb(COLORS.white));
    // Get first character of entity name, fallback to 'S' if no name
    const firstChar = entity?.name ? entity.name.charAt(0).toUpperCase() : 'S';
    // Center character in 7x7 square: square is 7x7, text position should be center (3.5, 3.5)
    pdfText(pdf, firstChar, iconX + 3.5, y + 8, { align: 'center' });

    // Entity name
    applyFont(pdf, 11);
    pdf.setTextColor(...hexToRgb(COLORS.neutralPrimary));
    if (isRTL) {
      pdfText(pdf, entity.name ?? '-', margin + contentWidth - 15, y + 9, {
        align: 'right',
      });
    } else {
      pdfText(pdf, entity.name ?? '-', margin + 15, y + 9);
    }

    // Detail rows
    let detailY = y + 17;
    const detailRows = [
      { label: `${labels.licenseKey}:`, value: '-' },
      {
        label: `${labels.assignedTeacher}:`,
        value: entity.assigned_teacher?.name ?? '-',
      },
      { label: `${labels.licenseExpiry}:`, value: '-' },
    ];

    for (const row of detailRows) {
      applyFont(pdf, 9);
      pdf.setTextColor(...hexToRgb(COLORS.neutralSecondary));
      if (isRTL) {
        pdfText(pdf, row.label, margin + contentWidth - 6, detailY, {
          align: 'right',
        });
        pdf.setTextColor(...hexToRgb(COLORS.neutralPrimary));
        pdfText(pdf, row.value, margin + 6, detailY, { align: 'left' });
      } else {
        pdfText(pdf, row.label, margin + 6, detailY);
        pdf.setTextColor(...hexToRgb(COLORS.neutralPrimary));
        pdfText(pdf, row.value, margin + contentWidth - 6, detailY, {
          align: 'right',
        });
      }
      detailY += 6;
    }

    y += cardHeight + 4;
  }

  return y;
}

function drawProgressSummary(
  pdf: jsPDF,
  data: ISubscriptionChildDetails,
  labels: IPdfLabels,
  y: number,
  margin: number,
  contentWidth: number,
  isRTL: boolean
): number {
  const pageWidth = 210;

  // Section header
  applyFont(pdf, 12, 'bold');
  pdf.setTextColor(...hexToRgb(COLORS.neutralPrimary));
  if (isRTL) {
    pdfText(pdf, labels.progressSummary, pageWidth - margin, y + 4, {
      align: 'right',
    });
  } else {
    pdfText(pdf, labels.progressSummary, margin, y + 4);
  }
  y += 10;

  const cardHeight = 50;
  pdf.setDrawColor(...hexToRgb(COLORS.neutralLight200));
  pdf.setLineWidth(0.3);
  pdf.roundedRect(margin, y, contentWidth, cardHeight, 4, 4, 'S');

  const progress = data.progress_summary?.overall_progress ?? 0;
  applyFont(pdf, 9);
  pdf.setTextColor(...hexToRgb(COLORS.neutralSecondary));

  if (isRTL) {
    pdfText(pdf, labels.overallProgress, pageWidth - margin - 5, y + 7, {
      align: 'right',
    });
    pdf.setTextColor(...hexToRgb(COLORS.neutralPrimary));
    pdfText(pdf, `${progress}%`, margin + 5, y + 7, { align: 'left' });
  } else {
    pdfText(pdf, labels.overallProgress, margin + 5, y + 7);
    pdf.setTextColor(...hexToRgb(COLORS.neutralPrimary));
    pdfText(pdf, `${progress}%`, margin + contentWidth - 5, y + 7, {
      align: 'right',
    });
  }

  drawProgressBar(
    pdf,
    margin + 5,
    y + 10,
    contentWidth - 10,
    2.5,
    progress,
    COLORS.neutralLight100,
    COLORS.brandDefault
  );

  // Three stat cards
  const cardY = y + 17;
  const innerPadding = 5;
  const cardGap = 3;
  const availableWidth = contentWidth - innerPadding * 2;
  const statCardW = (availableWidth - cardGap * 2) / 3;
  const statCardH = 26;
  const startX = margin + innerPadding;

  // RTL: reverse order of cards visually
  const cardDefs = [
    {
      label: labels.starsEarned,
      value: String(data.progress_summary?.stars_earned ?? 0),
      bg: COLORS.warningLight100,
      text: COLORS.warningSecondary,
    },
    {
      label: labels.gamesCompleted,
      value: String(data.progress_summary?.games_completed ?? 0),
      bg: COLORS.successLight50,
      text: COLORS.successPrimary,
    },
    {
      label: labels.avgScreenTime,
      value: formatSecondsToDuration(
        data.progress_summary?.average_screen_time
      ),
      bg: COLORS.indigoLight50,
      text: COLORS.indigoSecondary,
    },
  ];

  const orderedCards = isRTL ? [...cardDefs].reverse() : cardDefs;

  orderedCards.forEach((card, idx) => {
    drawStatCard(
      pdf,
      startX + idx * (statCardW + cardGap),
      cardY,
      statCardW,
      card.label,
      card.value,
      card.bg,
      card.text,
      statCardH
    );
  });

  y += cardHeight + 6;
  return y;
}

function drawActivityTimeline(
  pdf: jsPDF,
  activities: Array<{
    title: string;
    timeLabel: string;
    quarters?: number;
    rounds?: number;
  }>,
  labels: IPdfLabels,
  y: number,
  margin: number,
  contentWidth: number,
  pageHeight: number,
  isRTL: boolean
): number {
  const pageWidth = 210;

  y = checkPageBreak(pdf, y, 20, margin, pageHeight);
  applyFont(pdf, 12, 'bold');
  pdf.setTextColor(...hexToRgb(COLORS.neutralPrimary));
  if (isRTL) {
    pdfText(pdf, labels.activity, pageWidth - margin, y + 4, {
      align: 'right',
    });
  } else {
    pdfText(pdf, labels.activity, margin, y + 4);
  }
  y += 6;

  pdf.setDrawColor(...hexToRgb(COLORS.neutral200));
  pdf.setLineWidth(0.2);
  pdf.line(margin, y, margin + contentWidth, y);
  y += 6;

  if (!activities.length) {
    applyFont(pdf, 11);
    pdf.setTextColor(...hexToRgb(COLORS.neutralSecondary));
    pdfText(pdf, labels.noDataFound, margin + contentWidth / 2, y + 10, {
      align: 'center',
    });
    return y + 20;
  }

  const dotX = isRTL ? pageWidth - margin - 5 : margin + 5;
  const itemHeight = 10;

  for (let i = 0; i < activities.length; i++) {
    y = checkPageBreak(pdf, y, itemHeight, margin, pageHeight);

    if (i < activities.length - 1) {
      pdf.setDrawColor(...hexToRgb(COLORS.neutral200));
      pdf.setLineWidth(0.3);
      pdf.line(dotX, y + 4, dotX, y + itemHeight);
    }

    pdf.setDrawColor(...hexToRgb(COLORS.neutralLight200));
    pdf.setLineWidth(0.4);
    pdf.setFillColor(...hexToRgb(COLORS.white));
    pdf.circle(dotX, y + 3, 3, 'FD');
    pdf.setFillColor(...hexToRgb(COLORS.brandDefault));
    pdf.circle(dotX, y + 3, 1.8, 'F');

    applyFont(pdf, 10);
    pdf.setTextColor(...hexToRgb(COLORS.neutralPrimary));
    const maxTitleWidth = contentWidth - 50;
    const titleLines = pdf.splitTextToSize(activities[i].title, maxTitleWidth);

    const { quarters, rounds } = activities[i];

    if (isRTL) {
      pdfText(pdf, titleLines[0], pageWidth - margin - 12, y + 4, {
        align: 'right',
      });

      let badgeX =
        pageWidth - margin - 12 - pdf.getTextDimensions(titleLines[0]).w - 4;
      if (quarters != null) {
        const w = drawBadge(
          pdf,
          `Q${quarters}`,
          badgeX - 20,
          y + 2,
          '#faf5ff',
          COLORS.brandDefault,
          7
        );
        badgeX -= w + 3;
      }
      if (rounds != null) {
        drawBadge(
          pdf,
          `R${rounds}`,
          badgeX - 20,
          y + 2,
          COLORS.neutralLight100,
          COLORS.neutralSecondary,
          7
        );
      }

      applyFont(pdf, 8);
      pdf.setTextColor(...hexToRgb(COLORS.neutralSecondary));
      pdfText(pdf, activities[i].timeLabel, margin, y + 4, { align: 'left' });
    } else {
      pdfText(pdf, titleLines[0], margin + 12, y + 4);

      let badgeX = margin + 12 + pdf.getTextDimensions(titleLines[0]).w + 4;
      if (quarters != null) {
        const w = drawBadge(
          pdf,
          `Q${quarters}`,
          badgeX,
          y + 2,
          '#faf5ff',
          COLORS.brandDefault,
          7
        );
        badgeX += w + 3;
      }
      if (rounds != null) {
        drawBadge(
          pdf,
          `R${rounds}`,
          badgeX,
          y + 2,
          COLORS.neutralLight100,
          COLORS.neutralSecondary,
          7
        );
      }

      applyFont(pdf, 8);
      pdf.setTextColor(...hexToRgb(COLORS.neutralSecondary));
      pdfText(pdf, activities[i].timeLabel, margin + contentWidth, y + 4, {
        align: 'right',
      });
    }

    y += itemHeight;
  }

  return y;
}

/* -------------------- MAIN EXPORT -------------------- */

export async function exportChildLearningPdf(
  childData: ISubscriptionChildDetails,
  labels: IPdfLabels,
  stats: IPdfStatItem[],
  formattedActivities: Array<{
    title: string;
    timeLabel: string;
    quarters?: number;
    rounds?: number;
  }>,
  avatarUrl: string,
  isRTL = false
): Promise<void> {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageHeight = 297;
  const margin = 15;
  const contentWidth = 210 - margin * 2;
  let y = margin;
  // Load and embed required fonts
  try {
    // Try to load custom fonts, but fall back to built-in fonts if they don't exist
    let amiriLoaded = false;
    let aptosLoaded = false;

    try {
      const [amiri, aptos, aptosBold] = await Promise.all([
        fetchFontBase64(
          'https://cdn.jsdelivr.net/gh/google/fonts@main/ofl/amiri/Amiri-Regular.ttf'
        ).catch(() =>
          fetchFontBase64('/fonts/Amiri-Regular.ttf').catch(() => null)
        ),
        fetchFontBase64('/fonts/Aptos.ttf').catch(() => null),
        fetchFontBase64('/fonts/Aptos-Bold.ttf').catch(() => null),
      ]);

      if (amiri) {
        pdf.addFileToVFS('Amiri-Regular.ttf', amiri);
        pdf.addFont('Amiri-Regular.ttf', 'Amiri', 'normal');
        pdf.addFont('Amiri-Regular.ttf', 'Amiri', 'bold'); // No bold Amiri, fallback to regular
        amiriLoaded = true;
      }

      if (aptos) {
        pdf.addFileToVFS('Aptos.ttf', aptos);
        pdf.addFont('Aptos.ttf', 'Aptos', 'normal');
        aptosLoaded = true;
      }

      if (aptosBold) {
        pdf.addFileToVFS('Aptos-Bold.ttf', aptosBold);
        pdf.addFont('Aptos-Bold.ttf', 'Aptos', 'bold');
      }
    } catch (fontError) {
      console.warn(
        'Custom fonts not available, using built-in fonts:',
        fontError
      );
    }

    // Set font based on availability and RTL requirements
    if (isRTL && amiriLoaded) {
      PDF_FONT_NAME = 'Amiri';
    } else if (!isRTL && aptosLoaded) {
      PDF_FONT_NAME = 'Aptos';
    } else {
      // Fallback to built-in fonts
      PDF_FONT_NAME = 'helvetica';
    }
    applyFont(pdf);
  } catch (error) {
    console.error('Error loading fonts:', error);
    PDF_FONT_NAME = 'helvetica';
    applyFont(pdf);
  }

  // Load avatar image
  let avatarImg: string | null = null;
  if (avatarUrl) {
    try {
      avatarImg = await loadImage(avatarUrl);
    } catch {
      avatarImg = null;
    }
  }

  // Section 1: Title
  y = drawTitle(pdf, labels.title, y, margin, contentWidth);

  // Section 2: Profile Header
  y = await drawProfileHeader(
    pdf,
    childData,
    labels,
    avatarImg,
    y,
    margin,
    contentWidth,
    isRTL
  );

  // Section 3: Status Row
  y = drawStatusRow(pdf, childData, labels, y, margin, contentWidth, isRTL);

  // Section 4: Stats Grid
  y = drawStatsGrid(pdf, stats, y, margin, contentWidth, isRTL);

  // Section 5: Linked Entities
  y = drawLinkedEntities(
    pdf,
    childData,
    labels,
    y,
    margin,
    contentWidth,
    pageHeight,
    isRTL
  );

  // Section 6: Progress Summary
  y = checkPageBreak(pdf, y, 60, margin, pageHeight);
  y = drawProgressSummary(
    pdf,
    childData,
    labels,
    y,
    margin,
    contentWidth,
    isRTL
  );

  // Section 7: Activity Timeline
  drawActivityTimeline(
    pdf,
    formattedActivities,
    labels,
    y,
    margin,
    contentWidth,
    pageHeight,
    isRTL
  );

  const fileName =
    `${childData.full_name || labels.childrenLabel}-${labels.learningDetails}.pdf`.replace(
      /[\r\n"]/g,
      ''
    );
  pdf.save(fileName);
}
